import React, { useRef } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

/*
Props:
- value: { file: File|null, url: string|null }
- onChange: ({ file, url }) => void
- boxSize?: { h: number, w: number }    // 既定: 縦4:横3（w:200, h:150)
- maxBytes?: number                      // 既定: 2MB
- accept?: string[]                      // 既定: ["image/jpeg","image/png"]
- label?: string                         // 既定: "写真"
*/

const DEFAULT_BOX = { h: 200, w: 150 }; // 縦4 : 横3（3:4の枠）
const DEFAULT_MAX_BYTES = 2 * 1024 * 1024;
const DEFAULT_ACCEPT = ["image/jpeg", "image/png"];

export default function AvatarPicker({
  value,
  onChange,
  boxSize = DEFAULT_BOX,
  maxBytes = DEFAULT_MAX_BYTES,
  accept = DEFAULT_ACCEPT,
  label = "写真",
  onError,
}) {
  const inputRef = useRef(null);

  const openFile = () => inputRef.current && inputRef.current.click();

  const handleFile = (file) => {
    // 型チェック
    if (!accept.includes(file.type)) {
      onError && onError("画像は JPEG または PNG を選択してください。");
      return;
    }
    // サイズチェック
    if (file.size > maxBytes) {
      const mb = (maxBytes / (1024 * 1024)) | 0;
      onError && onError(`画像サイズは ${mb}MB 以下にしてください。`);
      return;
    }

    // 強制トリミング（中央クロップ）→ boxSize にリサイズ
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const targetW = boxSize.w;
      const targetH = boxSize.h;
      const targetRatio = targetW / targetH; // 例: 150/200 = 0.75（= 横3:縦4）

      // 元画像のクロップ領域（中央）を計算
      let sx = 0,
        sy = 0,
        sw = img.width,
        sh = img.height;
      const imgRatio = img.width / img.height;

      if (imgRatio > targetRatio) {
        // 画像が横に長い → 横を切り落とす
        sw = img.height * targetRatio;
        sx = (img.width - sw) / 2;
      } else {
        // 画像が縦に長い/同等 → 縦を切り落とす
        sh = img.width / targetRatio;
        sy = (img.height - sh) / 2;
      }

      // キャンバスに描画（リサイズ）
      const canvas = document.createElement("canvas");
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, targetW, targetH);

      // Blob → File → URL にして返す
      canvas.toBlob((blob) => {
        if (blob) {
          const newFile = new File([blob], file.name, { type: file.type });
          const newUrl = URL.createObjectURL(newFile);
          onChange({ file: newFile, url: newUrl });
        }
        URL.revokeObjectURL(url); // 元URLは破棄
      }, file.type);
    };
    img.onerror = () => {
      alert("画像の読み込みに失敗しました。別の画像をお試しください。");
      URL.revokeObjectURL(url);
    };
    img.src = url;
  };

  const onInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = ""; // 同じファイルでも再選択できるようにリセット
  };

  const showPreview = Boolean(value?.url);

  return (
    <Box sx={{ width: boxSize.w }}>
      <Typography sx={{ fontSize: 14, mb: "6px" }}>{label}</Typography>

      <Box sx={{ position: "relative", width: boxSize.w, height: boxSize.h }}>
        {/* プレビュー枠（クリックでファイル選択） */}
        <Box
          onClick={openFile}
          role="button"
          aria-label="写真を選択"
          tabIndex={0}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && openFile()}
          sx={{
            width: "100%",
            height: "100%",
            bgcolor: "#eeeeee",
            border: "1px solid #e0e0e0",
            overflow: "hidden",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "&:hover": { bgcolor: "#e9e9e9" },
          }}
        >
          {showPreview ? (
            <img
              src={value.url}
              alt="写真プレビュー"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          ) : (
            <Typography sx={{ fontSize: 38, color: "#666" }}>＋</Typography>
          )}
        </Box>

        {/* 右上の × ボタン（プレビュー中のみ） */}
        {showPreview && (
          <IconButton
            size="small"
            aria-label="画像を削除"
            onClick={(e) => {
              e.stopPropagation();
              onChange({ file: null, url: null });
            }}
            sx={{
              position: "absolute",
              top: -8,
              right: -8,
              bgcolor: "rgba(0,0,0,0.08)",
              "&:hover": { bgcolor: "rgba(0,0,0,0.16)" },
              width: 28,
              height: 28,
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}

        {/* 非表示 input */}
        <input
          ref={inputRef}
          type="file"
          accept={accept.join(",")}
          style={{ display: "none" }}
          onChange={onInputChange}
        />
      </Box>
      {/* 注意書き */}
      <Typography
        sx={{
          fontSize: 12,
          color: "text.secondary",
          mt: 1,
          whiteSpace: "nowrap", // 折り返し禁止
        }}
      >
        JPEG / PNG、最大 {maxBytes / (1024 * 1024)}MB、縦横比 4:3（表示{" "}
        {boxSize.h}×{boxSize.w}px）
      </Typography>
    </Box>
  );
}
